import { Component, computed, input, inject, ChangeDetectionStrategy } from '@angular/core';
import { DataPlatformConsentEvent } from '../../data-platform/data-platform.model';
import { DateService } from '../../shared/services/date-service';

export interface ConsentTypeSummary {
  labelKey: string;
  countKey: string;
  countParams: { count: number };
  count: number;
  showDetails: boolean;
  latest: DataPlatformConsentEvent;
  latestDate: Date;
  first?: DataPlatformConsentEvent;
  firstDate?: Date;
}

export interface ConsentRevisionGroup {
  key: string;
  versionDate?: string;
  revisionNumber?: number;
  privacyPolicyUrl?: string;
  entries: ConsentTypeSummary[];
}

const GAVE_CONSENT_LABEL_KEY = 'data-platform.consent-gave';
const READ_POLICY_LABEL_KEY = 'data-platform.consent-read-policy';
const ACCEPTED_POLICY_LABEL_KEY = 'data-platform.consent-accepted-policy';
const ENTRY_LABEL_ORDER = [GAVE_CONSENT_LABEL_KEY, ACCEPTED_POLICY_LABEL_KEY, READ_POLICY_LABEL_KEY];

function toLabelKey(goalName: string): string {
  if (goalName === 'Gave consent') {
    return GAVE_CONSENT_LABEL_KEY;
  }
  if (goalName === 'visitedpage-Read Privacy Policy') {
    return READ_POLICY_LABEL_KEY;
  }
  return ACCEPTED_POLICY_LABEL_KEY;
}

export function groupConsentEvents(
  events: DataPlatformConsentEvent[],
  toLocalTime: (utcTimestamp: string) => Date,
): ConsentRevisionGroup[] {
  const sorted = [...(events ?? [])].sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));

  const eventsByGroup = new Map<string, DataPlatformConsentEvent[]>();
  for (const event of sorted) {
    const key = `${event.versionDate ?? ''}|${event.revisionNumber ?? ''}`;
    const groupEvents = eventsByGroup.get(key);
    if (groupEvents) {
      groupEvents.push(event);
    } else {
      eventsByGroup.set(key, [event]);
    }
  }

  const groups: ConsentRevisionGroup[] = [];
  for (const [key, groupEvents] of eventsByGroup) {
    const eventsByLabel = new Map<string, DataPlatformConsentEvent[]>();
    for (const event of groupEvents) {
      const labelKey = toLabelKey(event.goalName);
      const labelEvents = eventsByLabel.get(labelKey);
      if (labelEvents) {
        labelEvents.push(event);
      } else {
        eventsByLabel.set(labelKey, [event]);
      }
    }

    const entries: ConsentTypeSummary[] = [];
    for (const labelKey of ENTRY_LABEL_ORDER) {
      const labelEvents = eventsByLabel.get(labelKey);
      if (!labelEvents) {
        continue;
      }
      const latest = labelEvents[0];
      const first = labelEvents.length > 1 ? labelEvents[labelEvents.length - 1] : undefined;
      entries.push({
        labelKey,
        countKey: labelEvents.length === 1 ? 'data-platform.consent-time' : 'data-platform.consent-times',
        countParams: { count: labelEvents.length },
        count: labelEvents.length,
        showDetails: labelKey === GAVE_CONSENT_LABEL_KEY,
        latest,
        latestDate: toLocalTime(latest.timestamp),
        first,
        firstDate: first ? toLocalTime(first.timestamp) : undefined,
      });
    }

    groups.push({
      key,
      versionDate: groupEvents.find((event) => event.versionDate)?.versionDate,
      revisionNumber: groupEvents.find((event) => event.revisionNumber != null)?.revisionNumber,
      privacyPolicyUrl: groupEvents.find((event) => event.privacyPolicyUrl)?.privacyPolicyUrl,
      entries,
    });
  }

  // Events without any version/revision land in the '|' group — always shown last.
  return groups.sort((a, b) => Number(a.key === '|') - Number(b.key === '|'));
}

@Component({
  selector: 'app-data-platform-consent-list',
  templateUrl: './data-platform-consent-list.component.html',
  styleUrls: ['./data-platform-consent-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataPlatformConsentListComponent {
  private readonly dateService = inject(DateService);

  public readonly consentEvents = input<DataPlatformConsentEvent[]>([]);
  protected readonly consentGroups = computed(() =>
    groupConsentEvents(this.consentEvents(), (utcTimestamp) => this.dateService.utcToLocalTime(utcTimestamp)),
  );
}
